<script setup lang="ts">
import { Amplitude } from '@seede/amplitude';
import { t } from '~/i18n';

const trackDesignNow = () => {
  Amplitude.event('nav_link_click', {
    link_text: t('landing_home.roles.cta'),
    link_target: '/signup',
    section: 'roles',
    page_path: window.location.href,
  });
};
</script>

<template>
  <section class="roles-section h-screen">
    <!-- 6x8 Grid -->
    <div class="roles-grid">
      <!-- Row 1 -->
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <!-- Row 2 -->
      <div class="grid-cell"></div>
      <div class="grid-cell cell-text-left cell-top span-2 cell-heading-1">
        <span class="grid-text">{{ $t('landing_home.roles.heading_1') }}</span>
      </div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <!-- Row 3 -->
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <!-- Row 4 -->
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <!-- Row 5 -->
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell cell-text-right cell-bottom span-2 cell-heading-2">
        <span class="grid-text">{{ $t('landing_home.roles.heading_2') }}</span>
      </div>
      <div class="grid-cell"></div>
      <!-- Row 6 -->
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <!-- Row 7 -->
      <div class="grid-cell"></div>
      <div class="grid-cell cell-text-left cell-bottom cell-desc span-2">
        <span class="grid-text-desc">{{ $t('landing_home.roles.description') }}</span>
      </div>
      <div class="grid-cell"></div>
      <div class="grid-cell cell-bottom cell-btn">
        <NuxtLink to="/signup" class="grid-btn" @click="trackDesignNow">{{
          $t('landing_home.roles.cta')
        }}</NuxtLink>
      </div>
      <div class="grid-cell"></div>
      <!-- Row 8 -->
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
      <div class="grid-cell"></div>
    </div>
  </section>
</template>

<style scoped>
.roles-section {
  position: relative;
  background: linear-gradient(
    to bottom,
    #94b3da 0%,
    #d0e3f6 15%,
    #f5f1e6 30%,
    #e8d5c3 45%,
    #97a4bc 60%,
    #395e94 75%,
    #08306f 90%,
    #02245d 100%
  );
  overflow: hidden;
}

.roles-section::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 200px 200px;
  mix-blend-mode: overlay;
  opacity: 0.3;
  z-index: 1;
}

/* Grid container */
.roles-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: repeat(8, 1fr);
  width: 100%;
  height: 100%;
  background-image:
    /* 5 vertical lines at 16.667%, 33.333%, 50%, 66.667%, 83.333% */
    linear-gradient(
      to right,
      transparent calc(100% / 6 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(100% / 6 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(100% / 6 + 0.5px),
      transparent calc(100% / 6 + 0.5px)
    ),
    linear-gradient(
      to right,
      transparent calc(200% / 6 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(200% / 6 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(200% / 6 + 0.5px),
      transparent calc(200% / 6 + 0.5px)
    ),
    linear-gradient(
      to right,
      transparent calc(50% - 0.5px),
      rgba(255, 255, 255, 0.12) calc(50% - 0.5px),
      rgba(255, 255, 255, 0.12) calc(50% + 0.5px),
      transparent calc(50% + 0.5px)
    ),
    linear-gradient(
      to right,
      transparent calc(400% / 6 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(400% / 6 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(400% / 6 + 0.5px),
      transparent calc(400% / 6 + 0.5px)
    ),
    linear-gradient(
      to right,
      transparent calc(500% / 6 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(500% / 6 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(500% / 6 + 0.5px),
      transparent calc(500% / 6 + 0.5px)
    ),
    /* 7 horizontal lines at 12.5%, 25%, 37.5%, 50%, 62.5%, 75%, 87.5% */
    linear-gradient(
        to bottom,
        transparent calc(100% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(100% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(100% / 8 + 0.5px),
        transparent calc(100% / 8 + 0.5px)
      ),
    linear-gradient(
      to bottom,
      transparent calc(200% / 8 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(200% / 8 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(200% / 8 + 0.5px),
      transparent calc(200% / 8 + 0.5px)
    ),
    linear-gradient(
      to bottom,
      transparent calc(300% / 8 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(300% / 8 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(300% / 8 + 0.5px),
      transparent calc(300% / 8 + 0.5px)
    ),
    linear-gradient(
      to bottom,
      transparent calc(50% - 0.5px),
      rgba(255, 255, 255, 0.12) calc(50% - 0.5px),
      rgba(255, 255, 255, 0.12) calc(50% + 0.5px),
      transparent calc(50% + 0.5px)
    ),
    linear-gradient(
      to bottom,
      transparent calc(500% / 8 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(500% / 8 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(500% / 8 + 0.5px),
      transparent calc(500% / 8 + 0.5px)
    ),
    linear-gradient(
      to bottom,
      transparent calc(600% / 8 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(600% / 8 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(600% / 8 + 0.5px),
      transparent calc(600% / 8 + 0.5px)
    ),
    linear-gradient(
      to bottom,
      transparent calc(700% / 8 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(700% / 8 - 0.5px),
      rgba(255, 255, 255, 0.12) calc(700% / 8 + 0.5px),
      transparent calc(700% / 8 + 0.5px)
    );
}

.grid-cell {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 0;
  overflow: visible;
}

.cell-text-left {
  justify-content: flex-start;
  text-align: left;
}

.cell-text-right {
  justify-content: flex-end;
  text-align: right;
}

.cell-top {
  align-items: flex-start;
}

.cell-bottom {
  align-items: flex-end;
}

.span-2 {
  grid-column: span 2;
}

@media (max-width: 768px) {
  .roles-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(8, 1fr);
    background-image:
      /* 1 vertical line at 50% */
      linear-gradient(
        to right,
        transparent calc(50% - 0.5px),
        rgba(255, 255, 255, 0.12) calc(50% - 0.5px),
        rgba(255, 255, 255, 0.12) calc(50% + 0.5px),
        transparent calc(50% + 0.5px)
      ),
      /* 7 horizontal lines */
        linear-gradient(
          to bottom,
          transparent calc(100% / 8 - 0.5px),
          rgba(255, 255, 255, 0.12) calc(100% / 8 - 0.5px),
          rgba(255, 255, 255, 0.12) calc(100% / 8 + 0.5px),
          transparent calc(100% / 8 + 0.5px)
        ),
      linear-gradient(
        to bottom,
        transparent calc(200% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(200% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(200% / 8 + 0.5px),
        transparent calc(200% / 8 + 0.5px)
      ),
      linear-gradient(
        to bottom,
        transparent calc(300% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(300% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(300% / 8 + 0.5px),
        transparent calc(300% / 8 + 0.5px)
      ),
      linear-gradient(
        to bottom,
        transparent calc(50% - 0.5px),
        rgba(255, 255, 255, 0.12) calc(50% - 0.5px),
        rgba(255, 255, 255, 0.12) calc(50% + 0.5px),
        transparent calc(50% + 0.5px)
      ),
      linear-gradient(
        to bottom,
        transparent calc(500% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(500% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(500% / 8 + 0.5px),
        transparent calc(500% / 8 + 0.5px)
      ),
      linear-gradient(
        to bottom,
        transparent calc(600% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(600% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(600% / 8 + 0.5px),
        transparent calc(600% / 8 + 0.5px)
      ),
      linear-gradient(
        to bottom,
        transparent calc(700% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(700% / 8 - 0.5px),
        rgba(255, 255, 255, 0.12) calc(700% / 8 + 0.5px),
        transparent calc(700% / 8 + 0.5px)
      );
  }

  .grid-cell {
    display: none;
  }

  .cell-heading-1 {
    display: flex;
    grid-row: 2;
    grid-column: 1 / -1;
    align-items: flex-start;
  }

  .cell-heading-2 {
    display: flex;
    grid-row: 4;
    grid-column: 1 / -1;
    align-items: flex-start;
  }

  .cell-desc {
    display: flex;
    grid-row: 7;
    grid-column: 1 / 2;
    align-items: flex-start;
  }

  .cell-btn {
    display: flex;
    grid-row: 8;
    grid-column: 1 / -1;
    align-items: flex-start;
  }
}

.grid-text {
  font-family: var(--font-boldonse), sans-serif;
  font-size: clamp(3rem, 4vw, 7rem);
  font-weight: 400;
  line-height: 128%;
  color: white;
  text-transform: uppercase;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

:global(.landing-page[data-locale='ja']) .grid-text {
  font-size: clamp(5.4rem, 7.2vw, 12.6rem);
}

.cell-desc {
  align-items: stretch;
  container-type: size;
}

.grid-text-desc {
  display: flex;
  align-items: flex-end;
  height: 100%;
  font-size: clamp(0.6rem, 18cqh, 1.5rem);
  font-weight: 300;
  line-height: 1.15;
  color: rgba(255, 255, 255, 0.9);
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.cell-btn {
  padding: 0;
}

.grid-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: clamp(0.875rem, 1vw, 1rem);
  font-weight: 500;
  color: white;
  background-color: rgba(255, 255, 255, 0.15);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s ease;
}

.grid-btn:hover {
  background-color: rgba(255, 255, 255, 0.12);
}
</style>
